import { useEffect, useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, MenuItem, Typography, Autocomplete } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarBodegas = () => {
  const navigate = useNavigate()
  const { state } = useLocation() // Recibimos la fila seleccionada desde la grilla
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Rescatamos los códigos originales (llave compuesta) por si el usuario los modifica
  const [invcodigoViejo, setInvcodigoViejo] = useState("")
  const [bodcodigoViejo, setBodcodigoViejo] = useState("")

  // Estado del formulario
  const [formData, setFormData] = useState({
    invcodigo: "",
    bodcodigo: "",
    boddescri: "",
    bodstatus: "A",
    bodserieblanco: 0,
    loccodigo: "",
  })

  useEffect(() => {
    // Precargar los inputs con los valores de la fila enviada desde la grilla
    if (state) {
      setFormData({
        invcodigo: state.invcodigo || "",
        bodcodigo: state.bodcodigo || "",
        boddescri: state.boddescri || "",
        bodstatus: state.bodstatus || "A",
        bodserieblanco: state.bodserieblanco || 0,
        loccodigo: state.loccodigo || "",
      })
      setInvcodigoViejo(state.invcodigo || "")
      setBodcodigoViejo(state.bodcodigo || "")
    } else {
      navigate(-1) // Retorno de seguridad si entran directo a la URL sin data
    }
  }, [state, navigate])

  // --- LÓGICA DEFENSIVA PARA INVENTARIOS ---
  const { data: rawInventarios, isLoading: isLoadingInventarios } = useQuery({
    queryKey: ["listaInventarios_Editar"],
    queryFn: async () => {
      try {
        const response = await api.get("/Bodegas/getListaInventarios")
        return response?.data?.data || response?.data || response || []
      } catch (error) {
        console.error("Error al cargar Inventarios:", error)
        return []
      }
    },
  })

  const inventariosValidados = Array.isArray(rawInventarios)
    ? rawInventarios
    : Array.isArray(rawInventarios?.data)
      ? rawInventarios.data
      : []

  const listaInventarios = inventariosValidados.map((item) => ({
    id: item.invcodigo || item.id,
    label: item.label || `${item.invcodigo} - ${item.invdescri || ""}`,
  }))
  // --- FIN LÓGICA DEFENSIVA ---

  // --- LÓGICA DEFENSIVA PARA LOCALIDADES ---
  const { data: rawLocalidades, isLoading: isLoadingLocalidades } = useQuery({
    queryKey: ["listaLocalidades_Editar"],
    queryFn: async () => {
      try {
        const response = await api.get("/Bodegas/getListaLocalidad")
        return response?.data?.data || response?.data || response || []
      } catch (error) {
        console.error("Error al cargar Localidades:", error)
        return []
      }
    },
  })

  const localidadesValidadas = Array.isArray(rawLocalidades)
    ? rawLocalidades
    : Array.isArray(rawLocalidades?.data)
      ? rawLocalidades.data
      : []

  const listaLocalidades = localidadesValidadas.map((item) => ({
    id: item.loccodigo || item.id,
    label: item.label || `${item.loccodigo} - ${item.locdescri || ""}`,
  }))
  // --- FIN LÓGICA DEFENSIVA ---

  const {
    mutateAsync: SaveEdicionBodega,
    isPending: isSavingEdicion,
    isError,
  } = useMutation({
    queryKey: ["isEditingBodega"],
    fn: async (data) => {
      const response = await api.post("/Bodegas/updateBodegas", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  // Manejar cambios en los campos del formulario con limpieza y topes
  const handleInputChange = (field, value) => {
    let finalValue = typeof value === "string" ? value.toUpperCase() : value

    if (field === "bodcodigo") finalValue = finalValue.slice(0, 3)
    else if (field === "boddescri") finalValue = finalValue.slice(0, 100)

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Preparar payload tal cual lo espera el backend para la edición de llave compuesta
    const payload = {
      invcodigoOld: invcodigoViejo,
      invcodigoNew: formData.invcodigo,
      bodcodigoOld: bodcodigoViejo,
      bodcodigoNew: formData.bodcodigo,
      boddescri: formData.boddescri,
      bodstatus: formData.bodstatus,
      bodserieblanco: formData.bodserieblanco,
      loccodigo: formData.loccodigo,
    }

    try {
      if (!payload.invcodigoNew.trim()) return showWarning("Debe seleccionar un Inventario")
      if (!payload.bodcodigoNew.trim()) return showWarning("El Código de la Bodega es obligatorio")
      if (!payload.boddescri.trim()) return showWarning("La Descripción de la Bodega es obligatoria")
      if (!payload.loccodigo.trim()) return showWarning("Debe seleccionar una Localidad")

      await SaveEdicionBodega(payload)
    } catch (error) {
      console.error("Error al editar la Bodega:", error)
      if (error?.code === "VALIDATION_ERROR") {
        showWarning(error.message)
      }
    }
  }

  // Acción dinámica de la barra de menú basada en permisos
  const actualizarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "GRABAR")

  // Retorna arreglo vacío si el permiso no existe, evitando el botón default
  const toolbarActions = actualizarAction
    ? [
        {
          label: actualizarAction.acccaption,
          key: actualizarAction.acccaption,
          icon: getIconComponent(actualizarAction.accnameicono, actualizarAction.acctipoico),
        },
      ]
    : []

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* Toolbar superior con el botón de Guardar/Actualizar */}
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingEdicion || isLoadingInventarios || isLoadingLocalidades}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mr: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Editar Bodega: {bodcodigoViejo}</b>
        </div>

        <CustomBackdrop isLoading={isSavingEdicion || isLoadingInventarios || isLoadingLocalidades} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos de la Bodega
            </Typography>
            <Grid container spacing={3}>
              {/* COMBO INVENTARIO */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={listaInventarios}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    listaInventarios.find((c) => c.id === formData.invcodigo) ||
                    (formData.invcodigo ? { id: formData.invcodigo, label: `${formData.invcodigo}` } : null)
                  }
                  onChange={(event, newValue) => handleInputChange("invcodigo", newValue ? newValue.id : "")}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Inventario *"
                      placeholder="Buscar inventario..."
                      InputLabelProps={{ shrink: true }}
                      error={isError && !formData.invcodigo}
                    />
                  )}
                />
              </Grid>

              {/* CÓDIGO BODEGA */}
              <Grid item xs={12} sm={2}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código Bodega *"
                  value={formData.bodcodigo}
                  onChange={(e) => handleInputChange("bodcodigo", e.target.value)}
                  error={isError && !formData.bodcodigo}
                  inputProps={{ maxLength: 3 }}
                  disabled
                />
              </Grid>

              {/* DESCRIPCIÓN */}
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.boddescri}
                  onChange={(e) => handleInputChange("boddescri", e.target.value)}
                  error={isError && !formData.boddescri}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              {/* ESTADO */}
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado"
                  value={formData.bodstatus}
                  onChange={(e) => handleInputChange("bodstatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>

              {/* SERIE BLANCO */}
              <Grid item xs={12} sm={4}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Serie Blanco"
                  type="number"
                  value={formData.bodserieblanco}
                  onChange={(e) => setFormData((prev) => ({ ...prev, bodserieblanco: parseInt(e.target.value) || 0 }))}
                />
              </Grid>

              {/* COMBO LOCALIDAD (OBLIGATORIO) */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={listaLocalidades}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    listaLocalidades.find((l) => l.id === formData.loccodigo) ||
                    (formData.loccodigo ? { id: formData.loccodigo, label: `${formData.loccodigo}` } : null)
                  }
                  onChange={(event, newValue) =>
                    setFormData((prev) => ({ ...prev, loccodigo: newValue ? newValue.id : "" }))
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Localidad *"
                      placeholder="Buscar localidad..."
                      InputLabelProps={{ shrink: true }}
                      error={isError && !formData.loccodigo}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarBodegas
